import React, { useRef, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';

// Animation states
export const ANIMATION_STATES = {
  IDLE: 'idle',
  SPEAKING: 'speaking',
  LISTENING: 'listening',
  THINKING: 'thinking',
  ALERT: 'alert'
};

// Error Boundary Component for GLB loading
class ModelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, webglContextLost: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.log('GLB model failed to load, using fallback:', error);
  }

  componentDidMount() {
    // Listen for WebGL context loss events
    const canvas = document.querySelector('canvas');
    if (canvas) {
      canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault(); // Prevent default behavior
        console.warn('⚠️ WebGL context lost - AURA 3D model may not render correctly');
        this.setState({ webglContextLost: true });
      });

      canvas.addEventListener('webglcontextrestored', () => {
        console.log('✅ WebGL context restored');
        this.setState({ webglContextLost: false, hasError: false });
      });
    }
  }

  render() {
    if (this.state.hasError || this.state.webglContextLost) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Load 3D model (if available)
function AuraModel({ animationState }) {
  // Load GLB model - will throw error if file doesn't exist
  const { scene } = useGLTF('/models/aura-robot.glb');
  const modelRef = useRef();
  
  useFrame((state) => {
    if (!modelRef.current) return;
    
    const time = state.clock.elapsedTime;
    
    // Apply animations to loaded model
    switch (animationState) {
      case ANIMATION_STATES.IDLE:
        modelRef.current.rotation.y = Math.sin(time * 0.5) * 0.1;
        modelRef.current.position.y = Math.sin(time * 0.8) * 0.05;
        break;
      case ANIMATION_STATES.SPEAKING:
        modelRef.current.rotation.y = Math.sin(time * 2) * 0.2;
        modelRef.current.position.y = Math.sin(time * 3) * 0.1;
        break;
      case ANIMATION_STATES.LISTENING:
        modelRef.current.rotation.y = Math.sin(time * 1) * 0.15;
        modelRef.current.position.y = Math.sin(time * 1.5) * 0.08;
        break;
      case ANIMATION_STATES.THINKING:
        modelRef.current.rotation.y += 0.01;
        modelRef.current.position.y = Math.sin(time * 2) * 0.1;
        break;
      case ANIMATION_STATES.ALERT:
        modelRef.current.position.y = Math.sin(time * 5) * 0.15;
        modelRef.current.rotation.y = Math.sin(time * 3) * 0.3;
        break;
      default:
        // Default case for any unknown animation state
        break;
    }
  });
  
  // Clone the scene to avoid issues with multiple instances
  const clonedScene = React.useMemo(() => scene.clone(), [scene]);
  // Scale the model (add this line)
  clonedScene.scale.set(1.5, 1.5, 1.5); // 1.2 = 20% larger, adjust as needed
  return <primitive object={clonedScene} ref={modelRef} />;
}

function RobotModel({ animationState, position = [0, 0, 0] }) {
  const meshRef = useRef();
  const eyeRef1 = useRef();
  const eyeRef2 = useRef();
  
  // Animate based on state
  useFrame((state) => {
    if (!meshRef.current) return;
    
    const time = state.clock.elapsedTime;
    
    switch (animationState) {
      case ANIMATION_STATES.IDLE:
        meshRef.current.rotation.y = Math.sin(time * 0.5) * 0.1;
        meshRef.current.position.y = position[1] + Math.sin(time * 0.8) * 0.05;
        break;
      case ANIMATION_STATES.SPEAKING:
        meshRef.current.rotation.y = Math.sin(time * 2) * 0.2;
        meshRef.current.position.y = position[1] + Math.sin(time * 3) * 0.1;
        // Animate mouth/head for speaking
        if (meshRef.current.children[0]) {
          meshRef.current.children[0].scale.y = 1 + Math.sin(time * 5) * 0.1;
        }
        break;
      case ANIMATION_STATES.LISTENING:
        meshRef.current.rotation.y = Math.sin(time * 1) * 0.15;
        meshRef.current.position.y = position[1] + Math.sin(time * 1.5) * 0.08;
        break;
      case ANIMATION_STATES.THINKING:
        meshRef.current.rotation.y += 0.01;
        meshRef.current.position.y = position[1] + Math.sin(time * 2) * 0.1;
        break;
      case ANIMATION_STATES.ALERT:
        meshRef.current.position.y = position[1] + Math.sin(time * 5) * 0.15;
        meshRef.current.rotation.y = Math.sin(time * 3) * 0.3;
        // Flash eyes
        if (eyeRef1.current && eyeRef2.current) {
          const flash = Math.sin(time * 10) > 0 ? 1 : 0.3;
          eyeRef1.current.material.emissiveIntensity = flash;
          eyeRef2.current.material.emissiveIntensity = flash;
        }
        break;
      default:
        break;
    }
  });

  // Eye color based on state
  const eyeColor = animationState === ANIMATION_STATES.ALERT ? '#FF0000' : 
                   animationState === ANIMATION_STATES.SPEAKING ? '#00FF00' :
                   animationState === ANIMATION_STATES.THINKING ? '#FFFF00' : '#00FF00';
  
  const bodyColor = animationState === ANIMATION_STATES.ALERT ? '#FF6B6B' : '#4A90E2';
  const emissiveColor = animationState === ANIMATION_STATES.ALERT ? '#FF0000' : '#2E5C8A';

  // Geometric robot fallback component
  const GeometricRobot = (
    <group ref={meshRef} position={position}>
      {/* Head */}
      <mesh position={[0, 1.5, 0]}>
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshStandardMaterial 
          color={bodyColor} 
          emissive={emissiveColor} 
          emissiveIntensity={0.3} 
        />
      </mesh>
      
      {/* Eyes */}
      <mesh ref={eyeRef1} position={[-0.2, 1.6, 0.41]}>
        <sphereGeometry args={[0.1, 16, 16]} />
        <meshStandardMaterial 
          color={eyeColor} 
          emissive={eyeColor}
          emissiveIntensity={0.8}
        />
      </mesh>
      <mesh ref={eyeRef2} position={[0.2, 1.6, 0.41]}>
        <sphereGeometry args={[0.1, 16, 16]} />
        <meshStandardMaterial 
          color={eyeColor} 
          emissive={eyeColor}
          emissiveIntensity={0.8}
        />
      </mesh>
      
      {/* Body */}
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[1, 1.5, 0.6]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>
      
      {/* Arms */}
      <mesh 
        position={[-0.7, 0.5, 0]} 
        rotation={[0, 0, animationState === ANIMATION_STATES.SPEAKING ? Math.sin(Date.now() * 0.01) * 0.3 : 0]}
      >
        <boxGeometry args={[0.2, 0.8, 0.2]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>
      <mesh 
        position={[0.7, 0.5, 0]} 
        rotation={[0, 0, animationState === ANIMATION_STATES.SPEAKING ? -Math.sin(Date.now() * 0.01) * 0.3 : 0]}
      >
        <boxGeometry args={[0.2, 0.8, 0.2]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>
      
      {/* Chest Panel (AURA logo area) */}
      <mesh position={[0, 0.8, 0.31]}>
        <boxGeometry args={[0.4, 0.3, 0.05]} />
        <meshStandardMaterial 
          color="#1a1a1a" 
          emissive="#4A90E2"
          emissiveIntensity={0.5}
        />
      </mesh>
    </group>
  );

  return (
    <ModelErrorBoundary fallback={GeometricRobot}>
      <Suspense fallback={null}>
        <AuraModel animationState={animationState} />
      </Suspense>
    </ModelErrorBoundary>
  );
}

export default function AuraRobot({ animationState = ANIMATION_STATES.IDLE, size = 200 }) {
  return (
    <Canvas
      camera={{ position: [0, 0, 3], fov: 45 }}
      style={{ width: '100%', height: '100%', background: 'transparent' }}
    >
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 5, 5]} intensity={1} />
      <pointLight position={[-5, -5, -5]} intensity={0.5} color="#4A90E2" />
      
      <RobotModel animationState={animationState} />
      
      <OrbitControls
        enableZoom={false}
        enablePan={false}
        autoRotate={animationState === ANIMATION_STATES.IDLE}
        autoRotateSpeed={0.5}
        minPolarAngle={Math.PI / 3}
        maxPolarAngle={Math.PI / 2.2}
      />
    </Canvas>
  );
}

